import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const inputCls =
  "rounded-none bg-obsidian border-olive-deep text-foreground placeholder:text-coyote/60 focus-visible:ring-1 focus-visible:ring-khaki focus-visible:border-khaki";

export const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", amount: "", description: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/invoices");
      setInvoices(res.data);
    } catch (err) {
      toast.error("FAILED TO LOAD INVOICES", { description: formatApiError(err) });
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/invoices", { ...form, amount: parseFloat(form.amount) });
      toast.success("INVOICE CREATED", { description: "Copy the pay link and send it to the customer." });
      setForm({ customer_name: "", customer_email: "", amount: "", description: "" });
      load();
    } catch (err) {
      toast.error("CREATE FAILED", { description: formatApiError(err) });
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (inv) => {
    const url = `${window.location.origin}/pay/${inv.id}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("PAY LINK COPIED", { description: `${inv.invoice_number} — send it to the customer.` }),
      () => toast.error("COPY FAILED", { description: url })
    );
  };

  return (
    <section className="mt-16" data-testid="invoices-section">
      <p className="overline-tactical text-coyote mb-4">/// BILLING OPERATIONS</p>
      <h2 className="font-display uppercase text-2xl sm:text-4xl tracking-tight mb-8" data-testid="invoices-heading">
        Invoices
      </h2>

      <form onSubmit={create} className="border border-olive-deep p-6 sm:p-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="invoice-create-form">
        <Input required placeholder="CUSTOMER NAME" value={form.customer_name} onChange={set("customer_name")} className={inputCls} data-testid="invoice-customer-name" />
        <Input required type="email" placeholder="CUSTOMER EMAIL" value={form.customer_email} onChange={set("customer_email")} className={inputCls} data-testid="invoice-customer-email" />
        <Input required type="number" min="0.01" step="0.01" placeholder="AMOUNT (USD)" value={form.amount} onChange={set("amount")} className={inputCls} data-testid="invoice-amount" />
        <Textarea required placeholder="WORK PERFORMED / DESCRIPTION" value={form.description} onChange={set("description")} rows={1} className={`${inputCls} resize-none`} data-testid="invoice-description" />
        <button
          type="submit"
          disabled={creating}
          data-testid="invoice-create-button"
          className="dogtag bg-olive text-white font-bold text-xs tracking-[0.2em] py-3 hover:bg-khaki hover:text-obsidian transition-colors duration-300 disabled:opacity-50"
        >
          {creating ? "CREATING..." : "[ CREATE INVOICE ]"}
        </button>
      </form>

      {invoices.length === 0 ? (
        <div className="border border-olive-deep p-10 text-center" data-testid="invoices-empty">
          <p className="text-xs text-coyote tracking-[0.2em]">NO INVOICES YET — CREATE ONE AFTER A JOB TO COLLECT PAYMENT</p>
        </div>
      ) : (
        <div className="border border-olive-deep overflow-x-auto" data-testid="invoices-table">
          <table className="w-full text-left text-xs min-w-[760px]">
            <thead>
              <tr className="bg-olive-deep/40 text-coyote">
                {["INVOICE", "CUSTOMER", "AMOUNT", "DESCRIPTION", "CREATED", "STATUS", "PAY LINK"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.25em] font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-olive-deep">
              {invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-olive-deep/20 transition-colors duration-200" data-testid={`invoice-row-${inv.invoice_number.toLowerCase()}`}>
                  <td className="px-4 py-3 font-bold text-khaki whitespace-nowrap">{inv.invoice_number}</td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-foreground whitespace-nowrap">{inv.customer_name}</div>
                    <div className="text-coyote">{inv.customer_email}</div>
                  </td>
                  <td className="px-4 py-3 font-display text-lg text-foreground whitespace-nowrap">${inv.amount.toFixed(2)}</td>
                  <td className="px-4 py-3 text-muted-foreground max-w-[240px]"><span className="line-clamp-2">{inv.description}</span></td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{inv.created_at ? format(new Date(inv.created_at), "MM/dd/yy") : "-"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] tracking-[0.2em] font-bold border px-2.5 py-1 ${inv.status === "PAID" ? "text-olive border-olive bg-olive/20" : "text-alert border-alert/50 bg-alert/10"}`} data-testid={`invoice-status-${inv.invoice_number.toLowerCase()}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {inv.status !== "PAID" && (
                      <button
                        onClick={() => copyLink(inv)}
                        data-testid={`invoice-copy-link-${inv.invoice_number.toLowerCase()}`}
                        className="text-[10px] tracking-[0.2em] border border-olive-deep px-3 py-1.5 text-khaki hover:bg-khaki hover:text-obsidian transition-colors duration-300 whitespace-nowrap"
                      >
                        COPY PAY LINK
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
